/**
 * Batching buffer — decides what travels together, and when.
 *
 * Grouping is a separate concern from delivery, so it lives here rather than
 * inside the HTTP transport: the transport decides *how* bytes leave the
 * browser, this decides *which* events go in one request. A console
 * transport wants neither.
 *
 * Three properties matter, and each exists because of a specific failure:
 *
 * - **Bounded.** Beyond `maxQueueSize` the oldest events are dropped. An
 *   unreachable collector then costs a fixed amount of memory instead of an
 *   array that grows for as long as the tab is open.
 * - **Chunked by bytes as well as count.** The ingest endpoint caps the
 *   request body. Counting events alone would let twenty verbose ones
 *   exceed it, and a rejected batch loses every event in it.
 * - **Drains fully on demand.** At `visibilitychange -> hidden` there is no
 *   later, so a flush empties the queue rather than sending one batch.
 *
 * @module logging/pipeline/batcher
 */

import type { FlushReason, WireEvent } from '../core/types';
import { diagnostic } from '../core/diagnostic';

const encoder = new TextEncoder();

export interface BatcherOptions {
  /** Most events in one delivery. */
  readonly batchSize: number;
  /** Longest a completed event waits before being delivered. */
  readonly flushIntervalMs: number;
  /** Queue ceiling, beyond which the oldest events are dropped. */
  readonly maxQueueSize: number;
  /** Serialized byte ceiling for one delivery. */
  readonly maxBodyBytes: number;
  /** Hands a grouped batch to a transport. Must not throw. */
  readonly deliver: (batch: readonly WireEvent[], reason: FlushReason) => void;
}

export interface Batcher {
  add(event: WireEvent): void;
  flush(reason: FlushReason): void;
  /** Queue depth. Diagnostics only. */
  readonly size: () => number;
}

export function createBatcher(options: BatcherOptions): Batcher {
  const queue: WireEvent[] = [];
  let timer: number | null = null;

  function cancelTimer(): void {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  }

  function scheduleFlush(): void {
    if (timer !== null) return;
    timer = window.setTimeout(() => {
      timer = null;
      flush('interval');
    }, options.flushIntervalMs);
  }

  /**
   * Split the queue into deliverable chunks, honouring both ceilings.
   *
   * A single event that exceeds `maxBodyBytes` on its own is sent alone
   * rather than dropped: the sanitizer's bounds make that impossible in
   * practice, so if it happens it is a bug worth seeing rejected at the
   * endpoint rather than swallowed here.
   */
  function chunk(events: readonly WireEvent[]): WireEvent[][] {
    const chunks: WireEvent[][] = [];
    let current: WireEvent[] = [];
    let bytes = 2; // the enclosing `[]`

    for (const event of events) {
      const size = encoder.encode(JSON.stringify(event)).length + 1; // + comma
      const wouldOverflow =
        current.length >= options.batchSize || bytes + size > options.maxBodyBytes;

      if (wouldOverflow && current.length > 0) {
        chunks.push(current);
        current = [];
        bytes = 2;
      }

      current.push(event);
      bytes += size;
    }

    if (current.length > 0) chunks.push(current);
    return chunks;
  }

  function flush(reason: FlushReason): void {
    cancelTimer();
    if (queue.length === 0) return;

    const pending = queue.splice(0, queue.length);
    for (const batch of chunk(pending)) {
      options.deliver(batch, reason);
    }
  }

  return {
    add(event: WireEvent): void {
      queue.push(event);

      if (queue.length > options.maxQueueSize) {
        // Drop the oldest. Newer events describe what the visitor is doing
        // now, which is the more useful half of a truncated record.
        const overflow = queue.length - options.maxQueueSize;
        queue.splice(0, overflow);
        diagnostic(`queue full, dropped ${overflow} oldest`);
      }

      if (queue.length >= options.batchSize) {
        flush('batch_full');
      } else {
        scheduleFlush();
      }
    },

    flush,
    size: () => queue.length,
  };
}
